import { useState, useMemo } from "react";
import { API, useApi } from "@/lib/api";
import { fmtCurrency, fmtNum } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { ChartInsight } from "@/components/common/ChartInsight";
import {
  ShieldCheck, FileCheck2, Activity, Wallet, PieChart as PieIcon, Coins, FileText,
  Presentation, CalendarRange, TrendingUp, Download,
} from "lucide-react";

const PRESETS = [["week", "Week"], ["month", "Month"], ["year", "Year"]];
const iso = (d) => (d ? d.toISOString().slice(0, 10) : undefined);

export default function ImpactAnalytics() {
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState(undefined); // {from,to}
  const [busy, setBusy] = useState("");

  const params = useMemo(() => {
    if (period === "custom" && range?.from && range?.to) return { period: "custom", start: iso(range.from), end: iso(range.to) };
    return { period };
  }, [period, range]);

  const { data, isLoading, error } = useApi(["impact", params], "/analytics/impact", params);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await fetch(`${API}/analytics/report/${fmt}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(params),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `navitra-impact-${params.period}.${fmt === "pptx" ? "pptx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(fmt === "pdf" ? "PDF report downloaded" : "Executive deck (PPTX) downloaded");
    } catch (e) { toast.error("Export failed — please try again."); }
    setBusy("");
  };

  if (error) return <ErrorState />;
  const h = data?.headline;

  return (
    <div className="space-y-5" data-testid="impact-analytics">
      <PageHeader eyebrow="Navitra Lens" title="Impact Analytics"
        subtitle="Navitra's measurable effect on governance, compliance, monitoring, budget & content risk.">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full bg-muted p-1">
            {PRESETS.map(([k, l]) => (
              <button key={k} data-testid={`period-${k}`} onClick={() => { setPeriod(k); }}
                className={"px-3 py-1 text-xs font-medium rounded-full transition-colors " + (period === k ? "bg-navy text-white" : "text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="period-custom" onClick={() => setPeriod("custom")}
                  className={"px-3 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1 transition-colors " + (period === "custom" ? "bg-navy text-white" : "text-muted-foreground hover:text-foreground")}>
                  <CalendarRange className="h-3.5 w-3.5" />Custom</button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar mode="range" selected={range} onSelect={(r) => { setRange(r); setPeriod("custom"); }} numberOfMonths={2} />
                <div className="px-3 py-2 text-xs text-muted-foreground border-t">
                  {range?.from && range?.to ? `${iso(range.from)} → ${iso(range.to)}` : "Pick a start and end date"}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={!!busy} data-testid="export-pdf">
            <FileText className="h-3.5 w-3.5 mr-1.5" />{busy === "pdf" ? "Preparing…" : "Export PDF"}
          </Button>
          <Button size="sm" className="bg-navy text-white hover:bg-navy/90" onClick={() => download("pptx")} disabled={!!busy} data-testid="export-pptx">
            <Presentation className="h-3.5 w-3.5 mr-1.5" />{busy === "pptx" ? "Preparing…" : "Generate deck"}
          </Button>
        </div>
      </PageHeader>

      {isLoading || !data ? <LoadingBlock /> : (
        <>
          <p className="text-xs text-muted-foreground -mt-1">{data.period_label} · {data.start} → {data.end}</p>

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Events governed" value={fmtNum(h.events_governed)} icon={Activity} testid="kpi-events" />
            <Kpi label="AI spend" value={fmtCurrency(h.total_spend)} sub={`${h.utilization}% of budget`} icon={Wallet} testid="kpi-spend" />
            <Kpi label="ROI this period" value={`${h.roi_multiple}x`} sub={`${fmtCurrency(h.roi_value)} value`} icon={TrendingUp} accent testid="kpi-roi" />
            <Kpi label="Savings captured" value={fmtCurrency(h.captured_savings)} sub={`${fmtCurrency(h.open_savings)} open`} icon={Coins} testid="kpi-savings" />
          </div>

          {/* Discipline breakdown */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Panel title="Governance" icon={ShieldCheck} color="#1A3A5C" rows={[
              ["Policies live", `${data.governance.policies_live} / ${data.governance.policies_total}`],
              ["Enforcement actions", fmtNum(data.governance.enforcement_actions)],
              ["Coaching moments", fmtNum(data.governance.coaching_moments)],
            ]} />
            <Panel title="Policy compliance" icon={FileCheck2} color="#0891B2" rows={[
              ["Compliance score", `${data.compliance.score}%`],
              ["Violations avoided", fmtNum(data.compliance.violations_avoided)],
              ["Sensitive hit rate", `${data.compliance.policy_hit_rate}%`],
            ]} />
            <Panel title="Monitoring" icon={Activity} color="#2563EB" rows={[
              ["Events monitored", fmtNum(data.monitoring.events_monitored)],
              ["Incidents (open/total)", `${data.monitoring.incidents_open} / ${data.monitoring.incidents_total}`],
              ["Shadow-AI events", fmtNum(data.monitoring.shadow_ai_events)],
            ]} />
            <Panel title="Budgeting" icon={Wallet} color="#B8962E" rows={[
              ["Total spend", fmtCurrency(data.budgeting.total_spend)],
              ["Token spend", fmtCurrency(data.budgeting.token_spend)],
              ["Seat spend", fmtCurrency(data.budgeting.seat_spend)],
            ]} />
            <Panel title="Content monitoring" icon={ShieldCheck} color="#DC2626" rows={[
              ["Sensitive events", fmtNum(data.content_monitoring.sensitive_events)],
              ["Blocked", fmtNum(data.content_monitoring.blocks)],
              ["Redacted", fmtNum(data.content_monitoring.redactions)],
            ]} />
            <Panel title="Compliance frameworks" icon={FileCheck2} color="#16A34A"
              rows={data.compliance.frameworks.slice(0, 3).map((f) => [f.framework, `${f.pct}%`])} />
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-navy" />Spend trend</CardTitle></CardHeader>
              <CardContent>
                <ChartInsight title="AI spend trend" app="Impact Analytics"
                  context={`AI spend across ${data.period_label.toLowerCase()}, totalling ${fmtCurrency(data.budgeting.total_spend)} (${data.budgeting.utilization}% of budget).`}
                  data={data.spend_trend.map((p) => ({ label: p.date, usd: p.usd }))}>
                  <BarChart data={data.spend_trend.map((p) => p.usd)} labels={data.spend_trend.map((p) => p.date)} />
                </ChartInsight>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PieIcon className="h-4 w-4 text-navy" />Allocation by department</CardTitle></CardHeader>
              <CardContent>
                <ChartInsight title="Cost allocation by department" app="Impact Analytics"
                  context={`Spend split across ${data.allocation.by_department.length} departments; ${data.allocation.by_department[0]?.label || "the top team"} leads.`}
                  data={data.allocation.by_department}>
                  <div className="space-y-2">
                    {data.allocation.by_department.slice(0, 6).map((d, i) => {
                      const max = data.allocation.by_department[0]?.usd || 1;
                      return (
                        <div key={d.label} data-testid={`alloc-${i}`}>
                          <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{d.label}</span><span className="font-mono-nav">{fmtCurrency(d.usd)}</span></div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-navy" style={{ width: `${(d.usd / max) * 100}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </ChartInsight>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className={"rounded-xl border p-4 " + (accent ? "bg-navy text-white border-navy" : "bg-card")}>
      <div className="flex items-center justify-between">
        <span className={"text-[11px] uppercase tracking-wide " + (accent ? "text-white/70" : "text-muted-foreground")}>{label}</span>
        <Icon className={"h-4 w-4 " + (accent ? "text-gold" : "text-navy")} />
      </div>
      <p className="font-mono-nav font-bold text-2xl tabular-nums mt-1">{value}</p>
      {sub && <p className={"text-[11px] mt-0.5 " + (accent ? "text-white/60" : "text-muted-foreground")}>{sub}</p>}
    </div>
  );
}

function Panel({ title, icon: Icon, color, rows }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><span className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: color }}><Icon className="h-3.5 w-3.5 text-white" /></span>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm border-b border-border/50 last:border-0 py-1"><span className="text-muted-foreground">{k}</span><span className="font-mono-nav font-semibold text-navy">{v}</span></div>
        ))}
      </CardContent>
    </Card>
  );
}

function BarChart({ data = [], labels = [] }) {
  const max = Math.max(...data, 1);
  return (
    <div className="h-40 flex items-end gap-0.5">
      {data.map((v, i) => (
        <div key={i} title={`${labels[i]}: ${fmtCurrency(v)}`} className="flex-1 rounded-t-sm bg-navy/70 hover:bg-navy transition-colors" style={{ height: `${(v / max) * 100}%` }} />
      ))}
    </div>
  );
}
